import clsx from 'clsx'
import { useEffect } from 'react'
import {
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { useHistory, useParams } from 'react-router-dom'
import { useMessage } from '@pepper/ui'

import KeywordDifficulty from 'container/Projects/LiveProjects/Project/IdeaLab/Keywords/KeywordDifficulty'
import VolumePill from 'components/idea-lab/VolumePill'

import LoadingState from './LoadingState'
import ErrorState from './ErrorState'

import { useKeywordThemes } from 'container/SEOTools/hooks/useKeywordThemes'
import { useCreateCategory } from 'utils/hooks/content-ideas/useCreateCategory'

import { pluralize } from '@pepper/utils/src/function/pluralize'

import s from './Themes.module.css'

type Props = {
  domainId: string
  companyId: string
  categoryName?: string
  categoryId?: string
}

export const Themes = ({
  domainId,
  categoryId,
  companyId,
  categoryName,
}: Props) => {
  const history = useHistory()
  const { showError } = useMessage()
  const { tab }: { tab: string } = useParams()
  const [__, setStep] = useQueryParam(
    'step',
    withDefault(StringParam, undefined)
  )
  const [queryParams, setQueryParams] = useQueryParams({
    category_id: withDefault(StringParam, undefined),
    theme_id: withDefault(StringParam, undefined),
    retry_category: withDefault(StringParam, undefined),
  })

  const { themes, isLoading, refetch, error } = useKeywordThemes({
    companyId,
    domainId,
    categoryId,
    enabled: !!companyId && !!domainId && !!categoryId,
  })

  const { mutateAsync: createCategory, isLoading: isCreating } =
    useCreateCategory()

  const handleRetryCreateCategory = async () => {
    if (!queryParams.retry_category) return
    setStep('themes')
    setQueryParams({
      category_id: undefined,
    })

    try {
      const response = await createCategory({
        category: queryParams.retry_category,
        domainId,
        companyId,
      })
      if (response.data) {
        setQueryParams({
          retry_category: undefined,
        })
        setQueryParams({
          category_id: response.data.id,
        })
      }
    } catch (_error) {
      showError(_error?.response?.data?.message)
    }
  }

  const handleThemeClick = (themeId: string) => {
    setStep(tab === 'keywords' ? 'keyword_suggestions' : 'topic_suggestions')
    setQueryParams(
      {
        theme_id: themeId,
      },
      'replaceIn'
    )
  }

  const showRetryCreateCategory = !!queryParams.retry_category
  const showLoadingState =
    (isLoading || !categoryId) && !showRetryCreateCategory
  const isContentIdeasFlow = history.location.pathname === '/content-ideas'

  useEffect(() => {
    return () => {
      setQueryParams(
        {
          retry_category: undefined,
        },
        'replaceIn'
      )
    }
  }, [setQueryParams])

  return (
    <div className={s.wrapper}>
      {error && !themes && (
        <ErrorState className={s.error} onRefetch={refetch} />
      )}
      {showRetryCreateCategory && (
        <ErrorState
          className={s.error}
          onRefetch={handleRetryCreateCategory}
          isLoading={isCreating}
        />
      )}
      <div className={s.header} title={categoryName}>
        {showLoadingState && 'Suggesting content themes'}
        {!showLoadingState &&
          themes &&
          `Suggested ${themes.length} themes for '${categoryName}'`}
      </div>
      {showLoadingState && <LoadingState />}
      <div
        className={clsx(s.themesWrapper, {
          [s.contentIdeasFlow]: isContentIdeasFlow,
        })}
      >
        {!showLoadingState &&
          themes?.map((record, index) => (
            <div
              key={record.id}
              className={s.card}
              role="none"
              onClick={() => handleThemeClick(record.id)}
              data-id={`theme-${index}-content-ideas`}
            >
              <div className={s.cardHeader}>
                {record.difficulty !== undefined && (
                  <KeywordDifficulty difficulty={record.difficulty} />
                )}
                <VolumePill value={record.volume} />
              </div>
              <div className={s.titleWrapper} title={record.theme}>
                <div className={s.title}>{record.theme}</div>
              </div>
              <div>
                <span className={s.keywords}>
                  {record.keyword_preview.join(', ').slice(0, 80)}
                </span>
                &nbsp;
                {record.total_keywords - record.keyword_preview.length > 0 && (
                  <span className={s.totalKeywords}>
                    +
                    {pluralize(
                      record.total_keywords - record.keyword_preview.length,
                      'keyword'
                    )}
                  </span>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}
